import { useEffect } from 'react';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router';

import { paths, patterns } from '@/app/router/paths';

import { copyFor } from './copy';
import { PublicReportContent } from './components/PublicReportContent';
import { PublicReportLayout } from './components/PublicReportLayout';
import { usePublicReport } from './usePublicReport';

/**
 * Klantpagina, publieke modus: `/r/:token/:slug?`, buiten de inlog. De koper
 * heeft geen account: de link uit de e-mail of sms is de sleutel, en die werkt
 * tot de datum die het bedrijf koos.
 *
 * Het adres in de link (`saffier-4-hoorn`) is alleen voor het oog. Ontbreekt het,
 * is het verouderd (een nieuwe versie verbeterde het adres) of verkeerd, dan zet
 * de pagina het stil goed zodra het rapport er is: dezelfde pagina, dezelfde
 * code, alleen de adresbalk verandert. Meestal deed de server dat al.
 *
 * Let op de aanspreekvorm: hier *u*, in de rest van de app *jij*.
 */
export function PublicReportPage() {
    const { token = '', slug = '' } = useParams<{ token: string; slug?: string }>();
    const state = usePublicReport(token);
    const copy = copyFor(state.language);
    const navigate = useNavigate();
    const { search, hash } = useLocation();
    /* Alleen op `/r/…`: een link van vroeger stuurt de router al door vóór deze pagina er is. */
    const onLinkAddress = useMatch(patterns.publicReport) !== null;
    const addressSlug = state.report?.addressSlug ?? '';

    useEffect(() => {
        document.title = state.report
            ? `${state.report.service} · ${state.report.address}`
            : copy.documentTitle;
    }, [state.report, copy.documentTitle]);

    useEffect(() => {
        if (!onLinkAddress || addressSlug === '' || slug === addressSlug) {
            return;
        }

        void navigate(
            { pathname: paths.publicReport(token, addressSlug), search, hash },
            { replace: true },
        );
    }, [onLinkAddress, addressSlug, slug, token, search, hash, navigate]);

    return (
        <PublicReportLayout tenant={state.tenant} language={state.language}>
            <PublicReportContent state={state} headingLevel={1} />
        </PublicReportLayout>
    );
}
